/**
 * WHO the apps are, and WHERE their links live.
 *
 * Universal Links (iOS) and App Links (Android) are a two-sided handshake: the
 * app declares the domains it will answer for, and the domain declares the apps
 * it trusts. Both sides have to name the SAME identifiers or the association
 * fails silently — the link just opens a browser and nobody gets an error. So
 * the identifiers live here once, and both sides read them:
 *
 *   - the app side  → `flashka-app/app.json` (`ios.associatedDomains`,
 *     `android.intentFilters`), pinned against these constants by
 *     `app-config.spec.ts`;
 *   - the domain side → `/.well-known/apple-app-site-association` and
 *     `/.well-known/assetlinks.json`, generated from `association.ts` at
 *     flashka-web build time.
 */

/** iOS bundle identifier — `expo.ios.bundleIdentifier`. */
export const IOS_BUNDLE_ID = 'ai.flashka.ios.dev';

/** Apple Developer Team ID — `expo.ios.appleTeamId`. */
export const APPLE_TEAM_ID = 'AG9GAR3738';

/** The `<TeamID>.<BundleID>` pair Apple calls an "app ID". */
export const IOS_APP_ID = `${APPLE_TEAM_ID}.${IOS_BUNDLE_ID}`;

/** Android application id — `expo.android.package`. */
export const ANDROID_PACKAGE_NAME = 'ai.flashka.android.dev';

/**
 * SHA-256 fingerprint of the Play APP SIGNING key — the certificate Google
 * re-signs every store build with, from Play Console → Test and release → App
 * integrity → Play app signing.
 *
 * NOT the upload key. EAS signs the bundle we upload with the upload key;
 * Google then re-signs it, and a device verifies against THIS one. Using the
 * upload key here is the single most common reason App Links never verify —
 * and it fails silently, because nothing on either side reports a mismatch.
 *
 * Checked in rather than held in an env var, deliberately: it is PUBLIC by
 * construction — `/.well-known/assetlinks.json` publishes it to the world, and
 * that is the whole point of the file. Nothing here is a credential. Keeping it
 * in the repo means Android links cannot break because a deploy forgot an env
 * var, and a change to it is reviewable in a diff like any other.
 *
 * A second key that must also verify (an internal-testing track signed with its
 * own key) is ADDED at build time via `ANDROID_APP_LINK_SHA256_FINGERPRINTS`;
 * see `androidAppLinkFingerprints`.
 */
export const ANDROID_APP_SIGNING_SHA256 =
  '9D:A6:48:2F:67:81:85:C8:D0:12:46:EB:36:9F:49:45:6D:1E:31:EB:DB:A6:43:E8:7A:3F:95:18:B0:1A:DB:6E';

/** Custom URL scheme — `expo.scheme`. `flashkaapp://deck/<id>`. */
export const APP_URL_SCHEME = 'flashkaapp';

/**
 * The ONE production origin every shareable Flashka link is built with.
 *
 * `www`, not the apex: the apex 301s to `www` (see the root `vercel.json`
 * redirect), and neither Apple's CDN nor Android's verifier follows a redirect
 * when fetching an association file — so a link minted on the apex costs a
 * round trip at best. Both hosts are still CLAIMED (see {@link FLASHKA_LINK_HOSTS}),
 * because links already in the wild point at both.
 */
export const FLASHKA_WEB_ORIGIN = 'https://www.flashka.ai';

/**
 * Every host whose `https://` links open the app. Both are claimed in the app's
 * associated domains AND serve their own association files — the apex→www
 * redirect is carved out for `/.well-known/` precisely so it can.
 */
export const FLASHKA_LINK_HOSTS = ['www.flashka.ai', 'flashka.ai'] as const;

/**
 * Hosts the LINK PARSER also accepts, so a preview deploy or a laptop dev
 * server can be pasted into the app and still resolve to a screen. Never
 * claimed as an app link — association files only exist on production.
 */
export const FLASHKA_DEV_LINK_HOST_PATTERNS: readonly RegExp[] = [
  /^localhost$/,
  /^127\.0\.0\.1$/,
  /^192\.168\.\d{1,3}\.\d{1,3}$/,
  /\.vercel\.app$/,
  /^([a-z0-9-]+\.)*flashka\.ai$/,
];
